/**
 * obsNoteTagOptions — DSM / behaviour sections clinicians can tag individual
 * Observation notes against.
 *
 * Why this exists
 * ---------------
 * The Criterion Mapping redesign lets clinicians tag bullet-point notes to
 * DSM-5 criteria (and two catch-all behaviour buckets) while they are still in
 * Task Administration or ADOS-informed Coding — instead of dragging each note
 * onto a criterion later. These ids are stored on each note as
 * `criteriaTags: string[]` and later drive the criteria-by-criteria review
 * panels (and, once accepted, the LLM paragraph inputs).
 *
 * Ids for A1–B4 match DSM5_DOMAINS. The two non-DSM buckets use stable snake /
 * short ids so they never collide with a criterion code.
 */

import { DSM5_DOMAINS } from './observationTasksConfig';

/** Non-DSM catch-all buckets that notes can still be tagged to. */
export const OBS_EXTRA_TAG_OPTIONS = [
  { id: 'self_reported', label: 'Self-Reported Behaviours' },
  { id: 'other', label: 'Other Behaviours' },
];

/**
 * Full Tag-picker catalogue, in display order.
 * Labels match the clinician-facing wording from the TOC Autism feedback.
 */
export const OBS_NOTE_TAG_OPTIONS = [
  ...DSM5_DOMAINS.map((d) => ({
    id: d.id,
    label: `${d.id}. ${d.shortTitle}`,
  })),
  ...OBS_EXTRA_TAG_OPTIONS,
];

/** Quick lookup: tag id → display label. */
export const OBS_NOTE_TAG_LABEL_BY_ID = Object.fromEntries(
  OBS_NOTE_TAG_OPTIONS.map((t) => [t.id, t.label]),
);

/**
 * Criterion-Mapping sidenav pages: DSM A1–B4 (with evidence decisions) plus
 * Self-Reported Behaviours and Other Behaviours (notes review only — no
 * evidence decision). The sidenav draws a divider between the DSM criteria
 * and these two behaviour buckets (see OBS_CRITERION_MAPPING_* helpers below).
 */
export const OBS_CRITERION_MAPPING_PAGES = [
  ...DSM5_DOMAINS.map((d) => ({
    id: d.id,
    badge: d.id,
    shortTitle: d.shortTitle,
    fullTitle: `${d.id}. ${d.shortTitle}`,
    /** True when this page records a Clear / Partial / No / Inconclusive decision. */
    hasEvidenceDecision: true,
  })),
  {
    id: 'self_reported',
    badge: 'SR',
    shortTitle: 'Self-Reported Behaviours',
    fullTitle: 'Self-Reported Behaviours',
    hasEvidenceDecision: false,
  },
  {
    id: 'other',
    badge: 'OB',
    shortTitle: 'Other Behaviours',
    fullTitle: 'Other Behaviours',
    hasEvidenceDecision: false,
  },
];

/** DSM A1–B4 pages (above the sidenav divider). */
export const OBS_CRITERION_MAPPING_DSM_PAGES = OBS_CRITERION_MAPPING_PAGES.filter(
  (p) => p.hasEvidenceDecision,
);

/** Self-Reported + Other Behaviours pages (below the sidenav divider). */
export const OBS_CRITERION_MAPPING_BEHAVIOUR_PAGES = OBS_CRITERION_MAPPING_PAGES.filter(
  (p) => !p.hasEvidenceDecision,
);

/** DSM criterion ids that require an evidence decision in Criterion Mapping. */
export const OBS_EVIDENCE_CRITERION_IDS = DSM5_DOMAINS.map((d) => d.id);
